import {
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { StorageKind, StorageDraftMap, StorageFormErrorMap } from './storage-form-types';
import styles from './StorageFields.module.css';

interface StorageFieldsProps {
  type: StorageKind;
  draft: StorageDraftMap[StorageKind];
  errors: StorageFormErrorMap;
  onChange: (field: string, value: string | boolean) => void;
}

function UrlPreview({ url }: { url: string }) {
  if (!url) return null;
  return <pre className={styles.urlPreview}>{url}</pre>;
}

const mask = '******';
const ftpUrl = (h: string, p: string, u: string) => `ftp://${u || '<user>'}:${mask}@${h || '<host>'}:${p || '21'}`;
const sftpUrl = (h: string, p: string, u: string) => `sftp://${u || '<user>'}:${mask}@${h || '<host>'}:${p || '22'}`;
const s3Url = (b: string, r: string, e?: string) => e ? `s3://${b || '<bucket>'}@${e}` : `s3://${b || '<bucket>'}.s3.${r || '<region>'}.amazonaws.com`;

export function StorageFields({ type, draft, errors, onChange }: StorageFieldsProps) {
  if (type === 's3') {
    const s = draft as StorageDraftMap['s3'];
    return (
      <div className={styles.grid}>
        <div className={styles.full}><UrlPreview url={s3Url(s.bucket, s.region, s.useCustomEndpoint ? s.endpoint : undefined)} /></div>
        <Field label="Name" error={errors.name}>{(fp) => <Input {...fp} value={s.name} onChange={(e) => onChange('name', e.target.value)} placeholder="Primary media archive" />}</Field>
        <Field label="Region" error={errors.region}>{(fp) => <Input {...fp} value={s.region} onChange={(e) => onChange('region', e.target.value)} placeholder="us-east-1" />}</Field>
        <Field label="Access key ID" error={errors.access_key_id}>{(fp) => <Input {...fp} value={s.access_key_id} onChange={(e) => onChange('access_key_id', e.target.value)} placeholder="AKIA..." />}</Field>
        <Field label="Secret access key" error={errors.secret_access_key}>{(fp) => <Input {...fp} type="password" value={s.secret_access_key} onChange={(e) => onChange('secret_access_key', e.target.value)} placeholder="••••••••" />}</Field>
        <div className={styles.full}><Field label="Bucket" error={errors.bucket}>{(fp) => <Input {...fp} value={s.bucket} onChange={(e) => onChange('bucket', e.target.value)} placeholder="media-output" />}</Field></div>
        <div className={`${styles.full} ${styles.switchRow}`}>
          <Switch checked={s.useCustomEndpoint} onCheckedChange={(v) => onChange('useCustomEndpoint', v)} id="custom-endpoint-toggle" />
          <label htmlFor="custom-endpoint-toggle" className={styles.switchLabel}>Use custom endpoint (S3-compatible / OSS)</label>
        </div>
        {s.useCustomEndpoint && (
          <div className={styles.full}>
            <Field label="Endpoint" description="Full HTTPS URL for the S3-compatible API endpoint." error={errors.endpoint}>
              {(fp) => <Input {...fp} value={s.endpoint} onChange={(e) => onChange('endpoint', e.target.value)} placeholder="https://s3.example.com" />}
            </Field>
          </div>
        )}
      </div>
    );
  }

  if (type === 'ftp') {
    const f = draft as StorageDraftMap['ftp'];
    return (
      <div className={styles.grid}>
        <div className={styles.full}><UrlPreview url={ftpUrl(f.host, f.port, f.username)} /></div>
        <Field label="Name" error={errors.name}>{(fp) => <Input {...fp} value={f.name} onChange={(e) => onChange('name', e.target.value)} placeholder="Archive FTP" />}</Field>
        <Field label="Host" error={errors.host}>{(fp) => <Input {...fp} value={f.host} onChange={(e) => onChange('host', e.target.value)} placeholder="ftp.example.com" />}</Field>
        <Field label="Port" error={errors.port}>{(fp) => <Input {...fp} type="number" value={f.port} onChange={(e) => onChange('port', e.target.value)} placeholder="21" />}</Field>
        <Field label="Username" error={errors.username}>{(fp) => <Input {...fp} value={f.username} onChange={(e) => onChange('username', e.target.value)} placeholder="ftp-user" />}</Field>
        <Field label="Password" error={errors.password}>{(fp) => <Input {...fp} type="password" value={f.password} onChange={(e) => onChange('password', e.target.value)} placeholder="••••••••" />}</Field>
        <div className={`${styles.full} ${styles.switchRow}`}>
          <Switch checked={f.pasv} onCheckedChange={(v) => onChange('pasv', v)} id="pasv-toggle" />
          <label htmlFor="pasv-toggle" className={styles.switchLabel}>Passive mode</label>
        </div>
      </div>
    );
  }

  if (type === 'sftp') {
    const s = draft as StorageDraftMap['sftp'];
    return (
      <div className={styles.grid}>
        <div className={styles.full}><UrlPreview url={sftpUrl(s.host, s.port, s.username)} /></div>
        <Field label="Name" error={errors.name}>{(fp) => <Input {...fp} value={s.name} onChange={(e) => onChange('name', e.target.value)} placeholder="Media SFTP" />}</Field>
        <Field label="Host" error={errors.host}>{(fp) => <Input {...fp} value={s.host} onChange={(e) => onChange('host', e.target.value)} placeholder="sftp.example.com" />}</Field>
        <Field label="Port" error={errors.port}>{(fp) => <Input {...fp} type="number" value={s.port} onChange={(e) => onChange('port', e.target.value)} placeholder="22" />}</Field>
        <Field label="Username" error={errors.username}>{(fp) => <Input {...fp} value={s.username} onChange={(e) => onChange('username', e.target.value)} placeholder="sftp-user" />}</Field>
        <Field label="Password" error={errors.password}>{(fp) => <Input {...fp} type="password" value={s.password} onChange={(e) => onChange('password', e.target.value)} placeholder="••••••••" />}</Field>
      </div>
    );
  }

  // http / webdav
  const h = draft as StorageDraftMap['http'];
  const isBasic = h.auth_type === 'basic';
  const hostDesc = type === 'webdav' ? 'Must start with http:// (WebDAV).' : 'Must start with http:// or https://.';

  return (
    <div className={styles.grid}>
      <Field label="Name" error={errors.name}>{(fp) => <Input {...fp} value={h.name} onChange={(e) => onChange('name', e.target.value)} placeholder={type === 'webdav' ? 'WebDAV archive' : 'HTTPS endpoint'} />}</Field>
      <Field label="Host / URL" description={hostDesc} error={errors.host}>{(fp) => <Input {...fp} value={h.host} onChange={(e) => onChange('host', e.target.value)} placeholder={type === 'webdav' ? 'http://storage.example.com' : 'https://storage.example.com'} />}</Field>
      <Field label="Authentication">
        {(fp) => (
          <Select value={h.auth_type} onValueChange={(v) => onChange('auth_type', v)}>
            <SelectTrigger {...fp}>
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="basic">Basic Auth</SelectItem>
            </SelectContent>
          </Select>
        )}
      </Field>
      {isBasic && (
        <>
          <Field label="Username" error={errors.username}>{(fp) => <Input {...fp} value={h.username} onChange={(e) => onChange('username', e.target.value)} placeholder="service-user" />}</Field>
          <Field label="Password" error={errors.password}>{(fp) => <Input {...fp} type="password" value={h.password} onChange={(e) => onChange('password', e.target.value)} placeholder="••••••••" />}</Field>
        </>
      )}
    </div>
  );
}
